import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { useUnloadWarning } from "@transfr-inc/dashboard-components/hooks";

import { useApiRequest } from "@lib/hooks";
import { Role } from "@models/role";
import { useCurrentUser, useRole } from "@queries";
import { useContainer } from "@src/contexts/container-context";
import { useAppStore } from "@src/stores/zustand/app-store";

import { useOrganizationDetails } from "@hooks/queries/organization";
import {
  exportOrganizationStats,
  getOrganizationDailyStats,
  getOrganizationStats as getOrganizationGeneralStats,
  getStatsFileName,
} from "@services";
import {
  DashboardBanner,
  DashboardInsights,
} from "@views/dashboard/components";

const getFilters = ({ classrooms, dateRange, moduleId }) => {
  const filters = { dateRange, moduleId };
  if (classrooms) {
    filters.classroomIds = classrooms.map((c) => c.classroomId);
  }

  return filters;
};

export const TsTab = () => {
  const defaultModuleOption = { displayName: "All Modules" };
  const defaultFilterCount = 1;

  const { organizationService } = useContainer();
  const currentUser = useCurrentUser();
  const role = useRole();
  const setDashboardLoaded = useAppStore((state) => state.setDashboardLoaded);
  const { organizationCode, userId } = currentUser || {};

  const [dateRange, setDateRange] = useState({});
  const [selectedClassrooms, setSelectedClassrooms] = useState();
  const [generalStats, setGeneralStats] = useState({ isLoading: true });
  const [dailyStats, setDailyStats] = useState({ isLoading: true });
  const { orgCode = organizationCode } = useParams();
  const [moduleSelected, setModuleSelected] = useState(defaultModuleOption);
  const [filterCount, setFilterCount] = useState(defaultFilterCount);

  const { data: organization } = useOrganizationDetails(orgCode);

  const { loading: downloadingFile, sendRequest: exportInsights } =
    useApiRequest(() => {
      const moduleId = moduleSelected?.moduleId ?? undefined;
      const filters = getFilters({
        classrooms: selectedClassrooms,
        dateRange,
        moduleId,
      });
      const fileName = getStatsFileName(organization?.name, dateRange);
      const code = role === Role.INSTRUCTOR.id ? userId : organization?.code;

      return exportOrganizationStats({
        organizationService,
        role,
        code,
        filters,
        fileName,
        getMultiOrg: false,
      });
    }, false);

  useUnloadWarning({ trigger: downloadingFile });

  useEffect(() => {
    if (!generalStats.isLoading && !dailyStats.isLoading) {
      setDashboardLoaded();
    }
  }, [generalStats, dailyStats]);

  useEffect(() => {
    const { startDate, endDate } = dateRange;
    const moduleId = moduleSelected?.moduleId ?? undefined;
    const filters = getFilters({
      classrooms: selectedClassrooms,
      dateRange,
      moduleId,
    });
    if (startDate && endDate) {
      getOrganizationStats(organization?.code, filters);
    }
  }, [dateRange, selectedClassrooms, moduleSelected]);

  const updateFilterCount = (classrooms, module) => {
    let count = defaultFilterCount;
    if (classrooms) {
      count += 1;
    }
    if (module) {
      count += 1;
    }
    setFilterCount(count);
  };

  const onApplyFilters = ({ classrooms, dateRange, module }) => {
    const { startDate, endDate } = dateRange || {};
    clearStats();
    setDateRange({ startDate, endDate });
    setSelectedClassrooms(classrooms?.map((c) => c.data));
    setModuleSelected(module);
    updateFilterCount(classrooms, module);
  };

  const clearStats = () => {
    setDailyStats({ isLoading: true });
    setGeneralStats({ isLoading: true });
  };

  const getOrganizationStats = (orgCode, filters) => {
    Promise.all([
      getOrganizationGeneralStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
      }).then((response) => setGeneralStats({ isLoading: false, response })),
      getOrganizationDailyStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
      }).then((response) => setDailyStats({ isLoading: false, response })),
    ]).catch(() => {
      setGeneralStats({ isLoading: false });
      setDailyStats({ isLoading: false });
    });
  };

  const onExportStatsData = () => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      exportInsights();
    }
  };
  return (
    <>
      <DashboardBanner product={Product.TS} />
      <DashboardInsights
        onApplyFilters={onApplyFilters}
        orgCode={organization?.code}
        classroomFilterEnabled
        userInfo={{ userId, role }}
        generalStats={generalStats}
        dailyStats={dailyStats}
        onExportData={onExportStatsData}
        exportDataDisabled={downloadingFile}
        moduleFilterEnabled
        filterCount={filterCount}
      ></DashboardInsights>
      <div className="notification-loader-container">
        {downloadingFile && (
          <Notification
            type={NotificationType.info}
            closable
            icon={["fa-solid", "spinner"]}
            iconConfig={{ spin: true }}
          >
            We’re preparing a download of{" "}
            <strong>
              {selectedClassrooms?.length > 1
                ? `${selectedClassrooms.length} classrooms'`
                : selectedClassrooms?.length === 1
                ? `${selectedClassrooms[0].name}'s`
                : `${organization?.name}'s`}
            </strong>{" "}
            insights. Please wait...
          </Notification>
        )}
      </div>
    </>
  );
};
